// src/components/skills/Skills.jsx
import React, { useState } from 'react';
import './skills.css';

const Skills = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [flippedCard, setFlippedCard] = useState(null);

  const categories = ['all', 'development', 'Video design (Coming soon)'];

  const skillsData = {
    frontend: {
      title: 'Frontend Development',
      icon: 'uil-react',
      level: 5,
      yearsExp: 'Since 2018',
      gradient: 'frontend-gradient',
      size: 'large',
      category: 'development',
      mainSkills: ['React', 'Tailwind CSS', 'TypeScript', 'Next.js'],
      projects: 'Live client sites',
      description: 'Shipping responsive React features end to end — from components to REST integration.',
      backContent: {
        recentProjects: ['spinosoftbits.com client portal', 'Small-business client websites', 'Responsive React front ends'],
        specialties: ['Component architecture', 'State management (Redux)', 'REST API integration']
      }
    },
    backend: {
      title: 'Backend Development',
      icon: 'uil-server',
      level: 4,
      yearsExp: 'Since 2020',
      gradient: 'backend-gradient',
      size: 'medium',
      category: 'development',
      mainSkills: ['Java','Node.js', 'C# ASP.NET Core',],
      projects: 'REST & webhook APIs',
      description: 'Node.js backends and REST/webhook APIs.',
      backContent: {
        recentProjects: ['Property-listing scanner backend — Mioym', 'Parking-kiosk REST integration — Automotion', 'Java language interpreter (lexer, parser, evaluator)'],
        specialties: ['Debugging under load', 'REST & webhook integrations', 'Auth & API design']
      }
    },
    database: {
      title: 'Databases & Data',
      icon: 'uil-database',
      level: 4,
      yearsExp: 'Since 2020',
      gradient: 'database-gradient',
      size: 'medium',
      category: 'development',
      mainSkills: ['PostgreSQL', 'SQL Server', 'MongoDB', 'GraphQL'],
      projects: 'Schema design & tuning',
      description: 'Schema design and slow-query tuning.',
      backContent: {
        recentProjects: ['PostgreSQL schema design & tuning — Mioym', 'MongoDB-backed app data', 'SQL Server (SSMS) relational work'],
        specialties: ['Query optimization', 'Indexing & schema design', 'Relational + NoSQL']
      }
    }
  };

  const currentlyLearning = [
    { name: 'Next.js 14', icon: '🚀' },
    { name: 'AI/ML Integration', icon: '🤖' },
    { name: 'GoLang', icon: '🌐' },
    { name: 'Rust', icon: '🦀' }
  ];

  const filteredSkills = Object.entries(skillsData).filter(([_, skill]) => 
    activeCategory === 'all' || skill.category === activeCategory
  );

  const handleCardClick = (skillKey) => {
    if (skillsData[skillKey].size !== 'small') {
      setFlippedCard(flippedCard === skillKey ? null : skillKey);
    }
  };

  const renderStars = (level) => {
    return Array.from({ length: 5 }, (_, i) => (
      <i key={i} className={`bx ${i < level ? 'bxs-star' : 'bx-star'} star-icon`}></i>
    ));
  };

  return (
    <section className="skills section" id="skills">
      <h2 className="section__title">Technical Skills</h2>
      <span className="section__subtitle">My technology stack & expertise</span>

      {/* Category Tabs */}
      <div className="skills__categories">
        {categories.map(cat => (
          <button
            key={cat}
            className={`category-tab ${activeCategory === cat ? 'active' : ''}`}
            onClick={() => setActiveCategory(cat)}
          >
            {cat.charAt(0).toUpperCase() + cat.slice(1)}
          </button>
        ))}
      </div>

      {/* Bento Grid Layout */}
      <div className="skills__container container">
        <div className="skills__bento-grid">
          {filteredSkills.map(([key, skill]) => (
            <div
              key={key}
              className={`skill-card skill-card--${skill.size} ${skill.gradient} ${flippedCard === key ? 'flipped' : ''}`}
              onClick={() => handleCardClick(key)}
            >
              <div className="skill-card__inner">
                {/* Front of card */}
                <div className="skill-card__front">
                  <div className="skill-card__header">
                    <i className={`uil ${skill.icon} skill-icon`}></i>
                    {skill.size !== 'small' && (
                      <span className="skill-years">{skill.yearsExp}</span>
                    )}
                  </div>
                  
                  <h3 className="skill-title">{skill.title}</h3>
                  
                  {skill.size !== 'small' && (
                    <>
                      <p className="skill-description">{skill.description}</p>
                      
                      <div className="skill-techs">
                        {skill.mainSkills.map((tech, idx) => (
                          <span key={idx} className="tech-badge">{tech}</span>
                        ))}
                      </div>
                    </>
                  )}
                  
                  <div className="skill-footer">
                    <div className="skill-level">
                      {renderStars(skill.level)}
                    </div>
                    {skill.size !== 'small' && (
                      <span className="skill-projects">{skill.projects}</span>
                    )}
                  </div>

                  {skill.size !== 'small' && (
                    <div className="flip-indicator">
                      <i className="bx bx-refresh"></i> Click to flip
                    </div>
                  )}
                </div>

                {/* Back of card (only for large/medium cards) */}
                {skill.backContent && (
                  <div className="skill-card__back">
                    <div className="back-header">
                      <h3>{skill.title}</h3>
                      <button className="close-flip" onClick={(e) => {
                        e.stopPropagation();
                        setFlippedCard(null);
                      }}>
                        <i className="uil uil-times"></i>
                      </button>
                    </div>

                    <div className="back-content">
                      <div className="back-section">
                        <h4>Recent Projects</h4>
                        <ul>
                          {skill.backContent.recentProjects.map((project, idx) => (
                            <li key={idx}>
                              <i className="bx bx-check-circle"></i> {project}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="back-section">
                        <h4>Specialties</h4>
                        <div className="specialty-tags">
                          {skill.backContent.specialties.map((specialty, idx) => (
                            <span key={idx} className="specialty-tag">{specialty}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Currently Learning Section */}
        <div className="currently-learning">
          <h3 className="learning-title">
            <i className="uil uil-book-open"></i> Currently Learning
          </h3>
          <div className="learning-grid">
            {currentlyLearning.map((item, idx) => (
              <div key={idx} className="learning-item">
                <span className="learning-icon">{item.icon}</span>
                <span className="learning-name">{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;